import date from '../../../../helpers/date';
import { TIKTOK_SOURCE, SHOPEE_SOURCE } from '../../../../constants/kickavenue';

import {
  LABEL_PW,
  LABEL_LL,
  KA,
  TIKTOK,
  SHOPEE,
} from './labelGeometry.constants';

// Confirmed by the backend handover for branch `mp-awb-ids` (2026-08-27). The AWB and the
// pickup code live on `sale_shipping` — NOT on `seller_awb_number`, which is the seller's
// own leg of the shipment, and not at the sale root, which carries neither.
const SHIPPING_AWB_FIELD = 'awb_number';
const SHIPPING_PICKUP_CODE_FIELD = 'pickup_code';

// The marketplace order id is stamped per channel, with the offer's own copy as fallback.
const TIKTOK_ORDER_ID_SOURCE_FIELD = 'tiktok_order_id';
const SHOPEE_ORDER_ID_SOURCE_FIELD = 'shopee_order_id';

const normaliseSource = (value) => (
  typeof value === 'string' ? value.trim().toLowerCase() : ''
);

// The design shows the date with a time component ("14 Aug 2026 · 15:00"); warehouse staff
// use it to tell same-day batches apart.
const LABEL_DATE_FORMAT = 'DD MMM YYYY · HH:mm';

// Pinned at the call site because `date()` does not actually apply a zone: its
// `moment.tz(tz)` builds a throwaway moment and discards it (`helpers/date.js:5`), so the
// value formats in whatever zone the operator's browser is in. The API serializes
// `created_at` as ISO-8601 UTC, which means a non-WIB browser prints an hour-shifted time —
// and on paper, past `printed_at` being committed, that cannot be corrected. Date-only
// formatting hid this; a clock time does not.
const LABEL_TIMEZONE = 'Asia/Jakarta';

const isBlank = (value) => value == null || value === '';

// Any missing value prints as '-'. The pickup code is handled separately and never prints as
// '-'; an empty pickup code omits its block entirely.
const valueOrDash = (value) => (isBlank(value) ? '-' : value);

const readShipping = (sale, field) => {
  const raw = sale && sale.sale_shipping ? sale.sale_shipping[field] : null;
  return raw == null ? '' : String(raw).trim();
};

const resolveSize = (sale) => {
  const size = sale.sale_product && sale.sale_product.size ? sale.sale_product.size.US : null;
  return isBlank(size) ? null : String(size);
};

const resolveRack = (sale) => {
  const userSell = sale.offer ? sale.offer.user_sell : null;
  const rack = userSell ? userSell.rack : null;
  return isBlank(rack) ? null : String(rack);
};

/**
 * "42 / LT2-45E" in the design. Either half can be missing, and the separator goes with it —
 * a bare "42 / " reads as a truncated rack rather than an absent one.
 */
const resolveSizeRack = (size, rack) => {
  const parts = [size, rack].filter(Boolean);
  return parts.length ? parts.join(' / ') : '-';
};

/**
 * The design labels this "CG ID" and its documentation frame calls it the "CG/User Sell ID":
 * consignment sales carry a consignment id, everything else is identified by its user sell.
 */
const resolveCgId = (sale) => {
  const consignmentId = sale.sale_product ? sale.sale_product.consignment_id : null;
  if (!isBlank(consignmentId)) return String(consignmentId);
  const userSell = sale.offer ? sale.offer.user_sell : null;
  return userSell && userSell.id != null ? String(userSell.id) : '';
};

const resolveMarketplaceOrderId = (sale) => {
  const stamped = sale[TIKTOK_ORDER_ID_SOURCE_FIELD] || sale[SHOPEE_ORDER_ID_SOURCE_FIELD];
  // The offer keeps its own copy, which is the one populated on the Anchanto route.
  const fallback = sale.offer ? sale.offer.mp_order_id : null;
  return valueOrDash(stamped || fallback);
};

/**
 * Single place any field on a sale is read for label rendering.
 */
export const resolveMarketplaceLabelData = (sale) => {
  const awb = readShipping(sale, SHIPPING_AWB_FIELD);
  const pickupCodeRaw = readShipping(sale, SHIPPING_PICKUP_CODE_FIELD);
  const size = resolveSize(sale);
  const rack = resolveRack(sale);
  const cgId = resolveCgId(sale);
  const hasProductInfo = !isBlank(sale.sale_product ? sale.sale_product.SKU : null)
    || !isBlank(size)
    || !isBlank(rack);

  // moment(undefined) is the current time, so an absent created_at would silently print
  // today's date instead of the '-' every other missing field prints.
  const parsedOrderDate = sale.created_at ? date(sale.created_at).tz(LABEL_TIMEZONE) : null;
  const orderDate = parsedOrderDate && parsedOrderDate.isValid()
    ? parsedOrderDate.format(LABEL_DATE_FORMAT)
    : '-';

  return {
    invoiceNumber: valueOrDash(sale.invoice_number),
    marketplaceOrderId: resolveMarketplaceOrderId(sale),
    awb: valueOrDash(awb),
    // Empty stays empty rather than becoming '-': the builder keys the whole pickup block
    // off this value being non-empty.
    pickupCode: pickupCodeRaw,
    orderDate,
    cgId,
    showCgId: !isBlank(cgId) && hasProductInfo,
    sku: valueOrDash(sale.sale_product ? sale.sale_product.SKU : null),
    sizeRack: resolveSizeRack(size, rack),
  };
};

// `^FH` makes `_XX` a hexadecimal byte escape. Encode ZPL control characters and command
// prefixes so values from the API cannot alter the surrounding format, and so an underscore
// in a SKU is printed as an underscore instead of being interpreted as an escape prefix.
const isZplEscapeCharacter = (character) => {
  const code = character.charCodeAt(0);
  return code <= 0x1F
    || code === 0x7F
    || character === '\\'
    || character === '^'
    || character === '~'
    || character === '_';
};

const zplText = (value) => Array.from(String(value), (character) => (
  isZplEscapeCharacter(character)
    ? `_${character.charCodeAt(0).toString(16).padStart(2, '0').toUpperCase()}`
    : character
)).join('');

// Zebra font 0 is approximately 0.62 dots wide per font-size unit. For single-line fields,
// reduce the font when a long value would exceed its ^FB width. This keeps order ids and AWBs
// readable without wrapping into the next fixed row.
const getFieldFont = (element, value) => {
  if (!element.fit || !value) return element.font;
  const estimatedWidth = value.length * element.font.w * 0.62;
  if (estimatedWidth <= element.w) return element.font;

  const fittedSize = Math.floor(element.font.h * element.w / estimatedWidth);
  // Below this point Zebra font 0 becomes operationally unreadable. Fail the build rather
  // than silently emitting a clipped identifier; the caller can show the print error and the
  // value can be corrected upstream.
  if (fittedSize < 16) {
    throw new Error(`Label value is too long for ${element.key}`);
  }
  const size = fittedSize;
  return { h: size, w: size };
};

const renderElement = (element, fields) => {
  if (element.kind === 'rule') {
    return `^FO${element.x},${element.y}^GB${element.w},${element.thickness},${element.thickness}^FS`;
  }
  if (element.kind === 'box') {
    return `^FO${element.x},${element.y}^GB${element.w},${element.h},${element.thickness}^FS`;
  }
  if (element.kind === 'group') {
    // The whole group stands or falls on one field — a bordered box with no code in it
    // would read as a PIN that failed to print rather than an order that has none.
    const gate = fields[element.requires];
    if (!gate) return '';
    return element.elements.map(el => renderElement(el, fields)).filter(Boolean).join('\n');
  }

  const text = element.kind === 'text' ? element.text : fields[element.key];
  if (isBlank(text)) return '';

  // ^FB gives the block the design's own width so R / C alignment lands where Figma put it.
  // Templates opt into a second line only for fields that are safe to wrap.
  const lines = element.lines || 1;
  const lineSpacing = element.lineSpacing || 0;
  const block = `^FB${element.w},${lines},${lineSpacing},${element.align},0`;
  const renderFont = element.kind === 'field' ? getFieldFont(element, String(text)) : element.font;
  return `^FO${element.x},${element.y}^A0N,${renderFont.h},${renderFont.w}${block}^FH^CI28^FD${zplText(text)}^FS^CI27`;
};

/**
 * Builds one full ZPL document from a template variant and the resolved label fields.
 */
const buildLabelZpl = (elements, fields) => {
  const body = elements.map(el => renderElement(el, fields)).filter(Boolean).join('\n');

  return `^XA
^JMA
^PW${LABEL_PW}
^LL${LABEL_LL}
^LS0
${body}
^PQ1,0,1,Y
^XZ`;
};

const buildPair = (template) => (data) => [
  buildLabelZpl(template.label1, data),
  buildLabelZpl(template.label2, data),
];

export const buildKickAvenueLabelZpl = buildPair(KA);
export const buildTiktokLabelZpl = buildPair(TIKTOK);
export const buildShopeeLabelZpl = buildPair(SHOPEE);

/**
 * Picks the label builder based on the sale's created source. TikTok and Shopee get their
 * own template; anything else (including Kick Avenue and Store) falls back to the KA template.
 */
export const getLabelBuilderForSource = (createdSource) => {
  // Normalised because the source is matched against lowercase literals while the backend
  // upper-cases the value. An unmatched source falls through to the Kick Avenue template
  // silently, so a casing mismatch would print the wrong template with no error.
  const source = normaliseSource(createdSource);
  if (source === TIKTOK_SOURCE) return buildTiktokLabelZpl;
  if (source === SHOPEE_SOURCE) return buildShopeeLabelZpl;
  return buildKickAvenueLabelZpl;
};

export const getLabelBuilderForSale = (sale) => {
  const marketplaceSource = normaliseSource(sale && sale.offer && sale.offer.mp_channel);
  return getLabelBuilderForSource(marketplaceSource || (sale && sale.created_source));
};
